// [전체 코드 미사용/주석 삭제 금지]
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from "config/apiConfig";

// CSS import [주석/미사용 코드 삭제 금지]
import 'styles/Font.css';

// Images import [주석/미사용 코드 삭제 금지]
import noimg from 'assets/images/logo/BranchNoImage.png';

// Component import [주석/미사용 코드 삭제 금지]
import TopNavigator from "components/layout/TopNavigator/TopNavigator";
import Footer from "components/layout/Footer/Footer";
import FloatingButton from "components/ui/FloatingButton/FloatingButton";
import usePreventInputScroll from "hooks/usePreventInputScroll";
// import usePreventZoom from "hooks/usePreventZoom";
import usePreventHorizontalScroll from "hooks/usePreventHorizontalScroll";
import { getTokenStatus } from "utils/tokenManager";


/**
 * 지점 상세 정보 API를 호출하는 함수. (GET /api/store/{brKey})
 * @param {string} brKey - 지점 키
 */
const fetchBranchInfoApi = async (brKey) => {
    const API_URL = `${API_BASE_URL}/store/${brKey}`;
    const { token } = getTokenStatus();
    /* ----------------------------------------------------- */
    /* ------------ [ 실제 API 호출 로직 (주석 처리) ] ----------- */
    /* ----------------------------------------------------- */
    try {
        const response = await fetch(API_URL, {
            method: 'GET',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Authorization': `Bearer ${token}`, },
        });

        if (!response.ok) {
            throw new Error(`API 호출 실패: HTTP 상태 ${response.status}`);
        }

        const data = await response.json();
        const res = data.data || data;

        const brInfo = res.brInfo;


        if (!brInfo) {
            throw new Error("API 응답 구조 오류: 지점 상세 데이터를 찾을 수 없습니다");
        }

        return brInfo;

    } catch (e) {
        console.error(`지점 상세 정보 API 호출 에러 (${brKey}):`, e);
        throw new Error('지점 상세 정보를 불러오는 데 실패했습니다');
    }
};


/* 지점 상세 정보 */
function BranchInfo() {
    // usePreventZoom();
    usePreventInputScroll();
    usePreventHorizontalScroll();

    const { brKey } = useParams();
    const navigate = useNavigate();

    // --- 상태 관리 ---
    const [brInfo, setBrInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(80);

    /*==============================동적 높이 측정===============================*/
    useEffect(() => {
        const measureHeight = () => {
            if (headerRef.current) {
                setHeaderHeight(headerRef.current.offsetHeight);
            }
        };

        measureHeight();

        let animationFrame;
        const debouncedMeasure = () => {
            if (animationFrame) cancelAnimationFrame(animationFrame);
            animationFrame = requestAnimationFrame(measureHeight);
        };

        window.addEventListener('resize', debouncedMeasure);
        return () => {
            window.removeEventListener('resize', debouncedMeasure);
            if (animationFrame) cancelAnimationFrame(animationFrame);
        };
    }, []);
    /*======================================================================*/

    /*=================================데이터 로딩=================================*/
    useEffect(() => {
        if (!brKey) {
            setLoading(false);
            setError("Error: 지점 키(brKey)가 없습니다");
            return;
        }

        const loadBranchInfo = async () => {
            setLoading(true);
            setError(null);
            try {
                const info = await fetchBranchInfoApi(brKey);
                // 추후 API 연동을 위한 변수(isProductAvailable) 처리
                // 위례중앙점(2)은 항상 'Y', 나머지는 API 값을 따르거나 없으면 'N'
                const processedInfo = {
                    ...info,
                    isProductAvailable: info.brKey === '2' ? 'Y' : (info.isProductAvailable || 'N')
                };
                setBrInfo(processedInfo);
            } catch (e) {
                setError(e.message || '상세 정보를 불러오는 데 실패했습니다');
                console.error(e);
            } finally {
                setLoading(false);
            }
        };

        loadBranchInfo();
    }, [brKey]);
    /*======================================================================*/

    const dynamicMainContentStyle = {
        ...staticStyles.mainContent,
        paddingTop: `${headerHeight}px`,
        paddingBottom: '100px', // Footer 높이 (BottomNavBar의 높이 100px)
        paddingLeft: '24px',
        paddingRight: '24px',
    };

    /*===============================콘텐츠 렌더링 함수===============================*/
    const renderBranchInfo = () => {
        // 로딩, 에러, 데이터 없음 상태 (기존 로직 유지)
        if (loading) {
            return (
                <div style={staticStyles.loadingWrapper}><p style={staticStyles.placeholderText}>지점 상세 정보를 불러오는 중...</p>
                </div>);
        }
        if (error) {
            return (<div style={staticStyles.loadingWrapper}><p
                style={{ ...staticStyles.placeholderText, color: '#FF5252' }}>{error}</p></div>);
        }
        if (!brInfo) {
            return (<div style={staticStyles.loadingWrapper}><p style={staticStyles.placeholderText}>선택하신 지점의 상세 정보가
                없습니다</p></div>);
        }

        // 정상 목록 렌더링
        const hasImages = brInfo.brImgs && Array.isArray(brInfo.brImgs) && brInfo.brImgs.length > 0;

        const imageSectionContent = hasImages ? (
            // 가로 스크롤 컨테이너 + Scroll Snap 적용
            <div className="ImageSection" style={staticStyles.imageSection}>
                {brInfo.brImgs.map((imgUrl, index) => (
                    <img
                        key={index}
                        src={imgUrl || noimg}
                        alt={`지점 이미지 ${index + 1}`}
                        style={staticStyles.branchImg}
                    />
                ))}
            </div>
        ) : (
            // 이미지가 없을 경우
            <div className="NoImageSection" style={staticStyles.noImageWrapper}>
                <img src={noimg} alt="이미지 없음" style={staticStyles.noImage} />
            </div>
        );


        return (
            <>
                {imageSectionContent}

                {/* 지점명/지점주소 */}
                <span style={staticStyles.titleText}>{brInfo.brName}</span>
                <span style={staticStyles.addressText}>{brInfo.brAddr}</span>

                <div className="horizonBar" style={staticStyles.horizonBar} />

                {/* 지점 설명 */}
                {/*<p style={{...staticStyles.contentDescription, alignSelf: 'flex-start'}}>지점 설명/상세 정보에 대한 내용이 표시됩니다</p>*/}
                <span className="brCont" style={{ color: '#FCFCFA' }}>{brInfo.brCont}</span>

                <div className="horizonBar" style={staticStyles.horizonBar} />

                {/* 운영 시간 */}
                <p style={{ ...staticStyles.contentDescription, alignSelf: 'flex-start' }}>운영 시간</p>
                {/* 평일 */}
                <div style={staticStyles.openingHoursRow}>
                    <span style={{ flexShrink: 0 }}>평일</span> {/* 글자가 줄바꿈되지 않도록 고정 */}
                    <span>{brInfo.brDayTime}</span>
                </div>

                {/* 주말 (동일한 간격 적용) */}
                <div style={staticStyles.openingHoursRow}>
                    <span style={{ flexShrink: 0 }}>주말</span>
                    <span>{brInfo.brHolyTime}</span>
                </div>
                <div className="horizonBar" style={staticStyles.horizonBar} />

                {/* 트레이너 정보 */}
                <div className="ManagerSection" style={staticStyles.managerSection}>
                    <span style={staticStyles.sectionTitle}>트레이너</span>
                    {/* brManager가 배열이 아닐 경우를 대비해 안전 확인 */}
                    {Array.isArray(brInfo.brManager) && brInfo.brManager.map((manager, index) => (
                        <div key={index} style={staticStyles.managerCard}>
                            <img src={manager.mnImg || noimg} alt={manager.mnName} style={staticStyles.managerImg} />
                            <div style={staticStyles.managerTextWrapper}>
                                <p style={staticStyles.managerName}>{manager.mnName}</p>
                                <p style={staticStyles.managerCont}>{manager.mnCont}</p>
                            </div>
                        </div>
                    ))}
                </div>

                <div style={{ height: '100px' }} />
                <Footer />
            </>
        );
    };
    /*======================================================================*/

    // 지점 선택하기 버튼 핸들러 (예시)
    const handleBrSelectClick = (brKey) => {
        // TODO: 결제 페이지로 이동
        // e.stopPropagation(); // 이벤트 버블링 방지
        navigate(`/prod/${brKey}`, {});
    };

    // 정상 렌더링
    return (
        <div className="backgroundWrapper" style={staticStyles.backgroundWrapper}>
            <div style={staticStyles.fixedHeaderWrapper} ref={headerRef}>
                <div className="statusBar" style={staticStyles.statusBar} />
                <TopNavigator title={brInfo ? brInfo.brName : ""} />
            </div>
            <main style={dynamicMainContentStyle}>
                {renderBranchInfo()}

            </main>
            {/* FloatingButton 컴포넌트 사용 (isProductAvailable이 'Y'인 경우만) */}
            {brInfo && brInfo.isProductAvailable === 'Y' && (
                <FloatingButton onClick={() => handleBrSelectClick(brKey)}>
                    상품 선택
                </FloatingButton>
            )}
        </div>
    )
};

// [!! staticStyles 객체 !!]
/** @type {React.CSSProperties} */
const staticStyles = {
    // --- 1. 전체 레이아웃 (공통) ---
    backgroundWrapper: {
        background: '#0D042D', width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
        alignItems: 'stretch', margin: '0 auto', boxSizing: 'border-box', overflowX: 'hidden', // 가로 스크롤 방지 (정상)
    },
    loadingWrapper: {
        background: '#0D042D', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100dvh',
    },
    // 고정 헤더 컨테이너
    fixedHeaderWrapper: {
        position: 'fixed', top: 0, left: 0,
        width: '100%', zIndex: 100, backgroundColor: '#0D042D',
    },
    statusBar: { flexShrink: 0, backgroundColor: 'transparent' },
    horizonBar: { width: '100%', height: '1px', background: '#515151', marginTop: '16px', marginBottom: '16px' },
    mainContent: {
        flex: 1, width: '100%', minHeight: 0, overflowY: 'auto', display: 'flex',
        flexDirection: 'column', gap: '19px', boxSizing: 'border-box',
    },
    // --- 2. 콘텐츠 스타일 ---
    titleText: {
        fontFamily: 'Pretendard', fontWeight: 700, fontSize: '20px', color: '#FCFCFA',
        alignSelf: 'flex-start', margin: '0',
    },
    addressText: {
        fontFamily: 'Pretendard', fontWeight: 400, fontSize: '14px', color: '#9A9A9A',
        alignSelf: 'flex-start', margin: '0', // marginBottom: '16px',
    },
    contentDescription: {
        fontFamily: 'Pretendard', fontWeight: 700, fontSize: '16px', color: '#FCFCFA', margin: '0',
    },
    openingHoursRow: {
        // 기존 폰트 스타일 유지
        fontFamily: 'Pretendard', fontWeight: 600, fontSize: '14px', color: '#FCFCFA',

        // 레이아웃 설정
        display: 'flex', alignItems: 'center', gap: '22px', width: '100%', marginTop: '4px',
    },
    sectionTitle: {
        fontFamily: 'Pretendard', fontWeight: 700, fontSize: '16px', color: '#FCFCFA',
        alignSelf: 'flex-start', margin: '0', // marginBottom: '15px',
    },
    // 매니저 섹션 (이하 동일)
    managerSection: { width: '100%', display: 'flex', flexDirection: 'column', gap: '15px', },
    managerCard: {
        width: '100%', display: 'flex', alignItems: 'center', padding: '10px', background: '#1B1931',
        borderRadius: '10px', overflow: 'hidden', boxSizing: 'border-box'
    },
    managerImg: {
        width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', marginRight: '15px', flexShrink: 0
    },
    managerTextWrapper: { display: 'flex', flexDirection: 'column', flex: 1 },
    managerName: { fontFamily: 'Pretendard', fontWeight: 700, fontSize: '16px', color: '#FCFCFA', margin: '0 0 4px 0' },
    managerCont: { fontFamily: 'Pretendard', fontWeight: 400, fontSize: '14px', color: '#A0A0A0', margin: '0' },


    // [!! 이미지 섹션 (가로 스크롤 컨테이너 + Scroll Snap) !!]
    imageSection: {
        // 🟢 수정: width: '100%'로 변경하고 음수 margin을 사용하여 좌우 패딩 상쇄
        width: '100%',
        height: '250px',
        marginTop: '20px',

        // mainContent의 좌우 패딩(24px)을 상쇄하여 화면 끝까지 확장
        // marginLeft: '-24px',
        marginRight: '-24px',

        display: 'flex',
        flexDirection: 'row',
        gap: '10px',

        overflowX: 'scroll',        // 가로 스크롤 활성화
        scrollSnapType: 'x mandatory',

        // 이미지 섹션 자체에 좌우 패딩을 적용하여 이미지 시작/끝 여백 확보
        // padding: '0 24px',
        boxSizing: 'border-box',

        WebkitOverflowScrolling: 'touch',
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
    },
    branchImg: {
        // 고정 너비를 주어 스냅 대상이 되도록 함
        // width: 'calc(100vw - 48px)', // 화면 너비 - 좌우 패딩 24px * 2
        width: '100%',
        height: '100%',
        borderRadius: '8px',
        objectFit: 'cover',
        flexShrink: 0,
        scrollSnapAlign: 'start',
    },

    // 이미지가 없을 때의 스타일
    noImageWrapper: {
        width: '100%', height: '250px', display: 'flex', justifyContent: 'center', alignItems: 'center',
        marginTop: '20px', backgroundColor: '#1B1931', borderRadius: '8px', overflow: 'hidden',
    },
    noImage: { width: '100px', height: '100px', opacity: 0.5, objectFit: 'contain' },
    placeholderText: { fontFamily: 'Pretendard', fontSize: '18px', color: '#FFFFFF', marginTop: '20px' },
};

export default BranchInfo;